import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCommentDraft } from '@/hooks/useCommentDraft';
import { getUserDisplayName, getUserInitial, getUserAvatarUrl } from '@/lib/utils';
import { MarkdownEditor } from './MarkdownEditor';
import { AuthModal } from './AuthModal';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Paperclip, X, Loader2 } from 'lucide-react';
import { S3Image } from '@/components/ui/s3-image';

interface CommentFormProps {
  postId: string;
  parentId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export function CommentForm({
  postId,
  parentId,
  onSuccess,
  onCancel,
  placeholder = 'Leave a comment',
}: CommentFormProps) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { saveDraft, getDraft, clearDraft } = useCommentDraft();
  const [value, setValue] = useState('');
  const [imageURLs, setImageURLs] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);

  // Restore draft on mount
  useEffect(() => {
    const draft = getDraft(postId, parentId);
    if (draft) {
      setValue(draft.value);
      setImageURLs(draft.imageURLs);
    }
    // Mark as initialized after state updates have been applied
    requestAnimationFrame(() => {
      initializedRef.current = true;
    });
  }, [postId, parentId, getDraft]);

  // Clear draft when input is emptied (only after initialization)
  useEffect(() => {
    if (initializedRef.current && !value.trim() && imageURLs.length === 0) {
      clearDraft(postId, parentId);
    }
  }, [value, imageURLs, postId, parentId, clearDraft]);

  const createCommentMutation = useMutation({
    mutationFn: (data: { postId: string; value: string; parentId?: string; imageURLs?: string[] }) =>
      api.comments.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['post', postId] });
      setValue('');
      setImageURLs([]);
      setError('');
      clearDraft(postId, parentId);
      toast.success(parentId ? 'Reply posted' : 'Comment posted');
      if (onSuccess) onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to post comment');
    },
  });

  const submitComment = () => {
    if (!value.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    createCommentMutation.mutate({
      postId,
      value: value.trim(),
      ...(parentId && { parentId }),
      ...(imageURLs.length > 0 && { imageURLs }),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!value.trim()) {
      setError('Comment cannot be empty');
      return;
    }

    if (!isAuthenticated) {
      // Save draft and show auth modal
      saveDraft(postId, parentId, value.trim(), imageURLs);
      setPendingSubmit(true);
      setShowAuthModal(true);
      return;
    }

    submitComment();
  };

  const handleAuthSuccess = async () => {
    setShowAuthModal(false);

    // Handle pending file uploads after auth
    if (pendingFiles.length > 0) {
      const files = pendingFiles;
      setPendingFiles([]);
      await uploadFiles(files);
    }

    // Handle pending comment submission after auth
    if (pendingSubmit) {
      setPendingSubmit(false);
      submitComment();
    }
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    setPendingFiles([]);
    // Keep pendingSubmit true so we can submit later if user logs in
  };

  const uploadFiles = async (files: File[]) => {
    setError('');
    setUploading(true);

    try {
      const uploadedUrls: string[] = [];
      for (const file of files) {
        const url = await api.upload.image(file, 'comments');
        uploadedUrls.push(url);
      }
      setImageURLs(prev => [...prev, ...uploadedUrls]);
    } catch (err: any) {
      setError(err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxImages = 3;
    if (imageURLs.length + files.length > maxImages) {
      setError(`You can only upload up to ${maxImages} images`);
      return;
    }

    const invalidFiles = files.filter(
      file => !file.type.startsWith('image/') || file.size > 10 * 1024 * 1024
    );

    if (invalidFiles.length > 0) {
      setError('Only image files under 10MB are allowed');
      return;
    }

    // If not authenticated, show auth modal and store files for later
    if (!isAuthenticated) {
      setPendingFiles(files);
      setShowAuthModal(true);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    await uploadFiles(files);
  };

  const handleRemoveImage = (indexToRemove: number) => {
    setImageURLs(imageURLs.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="flex gap-3">
          {/* User avatar - hidden on mobile to save space */}
          {user && (
            <Avatar className="h-8 w-8 shrink-0 hidden sm:flex">
              <AvatarImage
                src={getUserAvatarUrl(user) || undefined}
                alt={getUserDisplayName(user)}
              />
              <AvatarFallback className="text-xs" colorSeed={user.id}>{getUserInitial(user)}</AvatarFallback>
            </Avatar>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground mb-2 hidden sm:block">
              Share your use case: what you tried, what happened, what you expected.
            </p>
            <div
              className="border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
              onKeyDown={handleKeyDown}
            >
              {/* Editor */}
              <MarkdownEditor
                content={value}
                onChange={setValue}
                placeholder={placeholder}
                minimal
              />

              {/* Image previews */}
              {imageURLs.length > 0 && (
                <div className="flex gap-2 px-3 pb-2 border-t pt-2">
                  {imageURLs.map((url, index) => (
                    <div key={index} className="relative group">
                      <S3Image
                        src={url}
                        alt={`Upload ${index + 1}`}
                        className="w-16 h-16 object-cover rounded border"
                      />
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 h-5 w-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleRemoveImage(index)}
                        aria-label={`Remove image ${index + 1}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Controls at bottom-right */}
              <div className="flex items-center justify-end gap-1 px-2 pb-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploading || imageURLs.length >= 3}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || imageURLs.length >= 3}
                  aria-label="Attach images"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                </Button>
                {onCancel && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                    onClick={onCancel}
                    aria-label="Cancel"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  type="submit"
                  size="sm"
                  className="h-7 px-3"
                  disabled={createCommentMutation.isPending || !value.trim()}
                >
                  {createCommentMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Post'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Error message below input */}
        {error && (
          <p className="mt-1.5 text-sm text-destructive">{error}</p>
        )}
      </form>

      <AuthModal
        isOpen={showAuthModal}
        onClose={handleAuthModalClose}
        onSuccess={handleAuthSuccess}
        title="Sign in to post your comment"
      />
    </>
  );
}
